  import React, { useState, useEffect, useRef } from 'react';
  import { useLocation, useSearchParams } from 'react-router-dom';
  import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Paper,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TextField,
    Checkbox,
    FormControlLabel
  } from '@mui/material';
  import { 
    ChevronLeft, 
    ChevronRight, 
    Save, 
    Users, 
    Calendar, 
    RefreshCw,
    Edit,
    X,
    Trash2,
    Clock
  } from 'lucide-react';
  import { format } from 'date-fns';
  import FullCalendar from '@fullcalendar/react';
  import dayGridPlugin from '@fullcalendar/daygrid';
  import timeGridPlugin from '@fullcalendar/timegrid';
  import interactionPlugin from '@fullcalendar/interaction';
  import { EventClickArg, DateSelectArg } from '@fullcalendar/core';
  import axiosInstance from '../../utils/axiosConfig';
  import PatternDialog from './PatternDialog';
  import { 
    ROTA_OPTIONS, 
    getRotaOptionById, 
    shouldShowPatternDialog,
    isWeekday,
    getDayName,
    getMuiColorForOption
  } from '../../constants/rotaOptions';
  import { 
    getHolidaysInRange, 
    isIrishHoliday, 
    getHolidayInfo 
  } from '../../constants/irishHolidays';
  import { useAuth } from '../../contexts/AuthContext';
  import WhosWorkingToday from '../WhosWorkingToday';
  import LeaveSelectionDialog from './LeaveSelectionDialog';


  interface Personnel {
    _id: string;
    name: string;
    email: string;
    roles: string[];
    employmentStatus: string;
    profileImage?: string;
  }

  interface RotaEntry {
    _id?: string;
    personnelId: string | Personnel;
    date: string;
    isNew?: boolean;
    isModified?: boolean;
    startTime: string;
    endTime: string;
    status: string;
    notes?: string;
    isLocal?: boolean;
  }

  interface AllHandsEntry {
    _id: string;
    date: string;
    title: string;
    notes?: string;
    startTime: string;
    endTime: string;
    isAllHands: true;
  }

  interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    end: string;
    allDay: boolean;
    backgroundColor: string;
    textColor: string;
    borderColor: string;
    extendedProps: {
      personnelId: string;
      status: string;
      startTime: string;
      endTime: string;
      notes?: string;
      isLocal?: boolean;
      personnel: Personnel;
      entryId?: string;
      isHoliday?: boolean;
      holidayName?: string;
      isPending?: boolean;
      leaveRequestId?: string;
      isAllHands?: boolean;
      allHandsTitle?: string;
    };
  }

  interface PendingLeaveRequest {
  _id: string;
  personnelId: string;
  type: 'annual-leave' | 'time-lieu';
  dateStart: string;
  hoursRequested?: number;
  status: 'pending' | 'approved' | 'rejected';
}

  const ROTA_GROUPS = [
  {
    name: 'Work',
    options: ['working', 'programming', 'am', 'pm', 'cooler', 'working-unavail', 'meeting']
  },
  {
    name: 'Out & About',
    options: ['abroad', 'traveling', 'event']
  },
  {
    name: 'Leave',
    options: ['not-working', 'annual-leave', 'time-lieu', 'sick-leave']
  },
  {
    name: 'Holidays',
    options: ['company-leave', 'bank-holiday', 'unavailable']
  }
];

  const RotaBuilder: React.FC = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [currentMonth, setCurrentMonth] = useState(() => {
      const m = searchParams.get('month');
      if (m) {
        const d = new Date(`${m}-01`);
        if (!isNaN(d.getTime())) return d;
      }
      return new Date();
    });
    const location = useLocation();
    const [selectedPersonnel, setSelectedPersonnel] = useState('');
    const [personnel, setPersonnel] = useState<Personnel[]>([]);
    const [viewMode, setViewMode] = useState<'single' | 'multi'>('single');
    const [rotaEntries, setRotaEntries] = useState<RotaEntry[]>([]);
    const [originalEntries, setOriginalEntries] = useState<RotaEntry[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    
    const [selectedDay, setSelectedDay] = useState<Date | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [calendarKey, setCalendarKey] = useState(0);
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const [shouldScrollToCalendar, setShouldScrollToCalendar] = useState(false);

    // Delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState<CalendarEvent | null>(null);
    const [deleting, setDeleting] = useState(false);
    
    // Time In Lieu dialog
    const [customTimeDialogOpen, setCustomTimeDialogOpen] = useState(false);
    const [customStatusId, setCustomStatusId] = useState<string | null>(null);
    const [tilStartTime, setTilStartTime] = useState('10:00');
    const [tilEndTime, setTilEndTime] = useState('13:00');
    const [tilDate, setTilDate] = useState<Date | null>(null);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [leaveDialogDate, setLeaveDialogDate] = useState<Date | null>(null);
    const [pendingRequests, setPendingRequests] = useState<PendingLeaveRequest[]>([]);

    // Meeting notes (for individual meeting entries)
    const [meetingNotes, setMeetingNotes] = useState('');

    // All-hands meeting dialog
    const [allHandsDialogOpen, setAllHandsDialogOpen] = useState(false);
    const [allHandsTitle, setAllHandsTitle] = useState('');
    const [allHandsNotes, setAllHandsNotes] = useState('');
    const [allHandsStartTime, setAllHandsStartTime] = useState('10:00');
    const [allHandsEndTime, setAllHandsEndTime] = useState('11:00');
    const [allHandsDate, setAllHandsDate] = useState<Date | null>(null);
    const [allHandsEntries, setAllHandsEntries] = useState<AllHandsEntry[]>([]);
    const [savingAllHands, setSavingAllHands] = useState(false);

    // Pattern dialog state
    const [showPatternDialog, setShowPatternDialog] = useState(false);
    const [patternOption, setPatternOption] = useState<string | null>(null);
    const [patternDay, setPatternDay] = useState<{ 
      date: Date; 
      dayOfWeek: number; 
      dayName: string 
    } | null>(null);

    const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

    // Get time slots for status
    const getTimeSlotForStatus = (status: string) => {
      const option = getRotaOptionById(status);
      if (option && option.startTime && option.endTime) {
        return {
          startTime: option.startTime,
          endTime: option.endTime
        };
      }
      return { startTime: '10:00', endTime: '18:00' };
    };

      const countedStatuses = ['working', 'working-unavail', 'cooler', 'pm', 'am', 'event', 'programming'];
      const leaveStatuses = ['annual-leave', 'sick-leave', 'company-leave', 'time-lieu', 'not-working', 'unavailable', 'bank-holiday'];


    // Create custom time slot entry
const createCustomTimeEntry = (personnelId: string, date: Date, startTime: string, endTime: string, status: string, notes?: string) => {
  const dateStr = getLocalDateString(date);

  const newEntry: RotaEntry = {
    personnelId,
    date: dateStr,
    startTime,
    endTime,
    status,
    notes: notes || '',
    isLocal: true,
    isNew: true,
    isModified: true
  };

  setRotaEntries(prev => [...prev, newEntry]);
  setHasUnsavedChanges(true);
};

    // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const personnelResponse = await axiosInstance.get('/api/rota/personnel');
        
        // ⭐ FILTER: Only show employees
        const employees = personnelResponse.data.filter(
          (p: Personnel) => p.employmentStatus === 'employee'
        );
        
        setPersonnel(employees);

        if (!selectedPersonnel && user?.email) {
          const currentUserPersonnel = employees.find(
            (p: Personnel) => p.email === user.email
          );
          if (currentUserPersonnel) {
            setSelectedPersonnel(currentUserPersonnel._id);
          } else if (employees.length > 0) {
            setSelectedPersonnel(employees[0]._id);
          }
        }

        await Promise.all([loadRotaData(), loadPendingRequests()]);

      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load rota data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentMonth, user?.email]);

  useEffect(() => {
  // when pending requests update, refresh the calendar view
  setCalendarKey(k => k + 1);
}, [pendingRequests]);

    // Load rota data
const loadRotaData = async () => {
  try {
    const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const response = await axiosInstance.get('/api/rota', {
      params: {
        startDate: getLocalDateString(startDate),
        endDate: getLocalDateString(endDate)
      }
    });

    const allData: any[] = response.data;

    // Separate all-hands entries from personal rota entries
    const allHandsRaw = allData.filter((e: any) => e.isAllHands);
    const personalRaw = allData.filter((e: any) => !e.isAllHands && e.personnelId);

    const entriesArray: RotaEntry[] = personalRaw.map((entry: any) => {
      const personnelId = typeof entry.personnelId === 'string'
        ? entry.personnelId
        : entry.personnelId._id;

      let dateStr = entry.date;
      if (typeof dateStr !== 'string') {
        dateStr = new Date(entry.date).toISOString().split('T')[0];
      } else if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }

      return { ...entry, date: dateStr, personnelId, isLocal: false };
    });

    const allHandsArray: AllHandsEntry[] = allHandsRaw.map((e: any) => ({
      _id: e._id,
      date: typeof e.date === 'string' ? e.date.split('T')[0] : new Date(e.date).toISOString().split('T')[0],
      title: e.title || 'Staff Meeting',
      notes: e.notes || '',
      startTime: e.startTime,
      endTime: e.endTime,
      isAllHands: true
    }));

    setRotaEntries(entriesArray);
    setOriginalEntries([...entriesArray]);
    setAllHandsEntries(allHandsArray);
    setHasUnsavedChanges(false);
    
  } catch (err: any) {
    setError('Failed to load rota entries');
  }
};

const loadPendingRequests = async () => {
  try {
    const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const response = await axiosInstance.get('/api/leave-requests/pending-for-rota', {
      params: {
        personnelId: viewMode === 'single' ? selectedPersonnel : undefined,
        startDate: getLocalDateString(startDate),
        endDate: getLocalDateString(endDate)
      }
    });
    console.log('📋 Fetched pending requests:', response.data);

    const raw = response.data;
    const data = Array.isArray(raw)
      ? raw
      : raw?.requests || raw?.data || [];

    // Normalize
    const normalized = data.map((r: any) => ({
      _id: r._id,
      personnelId: r.personnelId?._id || r.personnelId,
      type: r.type || r.leaveType,
      dateStart: r.dateStart || r.date || r.startDate,
      hoursRequested: r.hoursRequested ?? r.hours,
      status: r.status || 'pending'
    }));

    setPendingRequests(normalized);

    // Force refresh of events after update
    setCalendarKey((k) => k + 1);
  } catch (err: any) {
    console.error('Failed to load pending requests:', err);
  }
};

    // Helper functions
    const getRotaKey = (personnelId: string, date: string | Date, startTime: string): string => {
      let dateStr: string;
      
      if (typeof date === 'string') {
        dateStr = date.includes('T') ? date.split('T')[0] : date;
      } else {
        dateStr = date.toISOString().split('T')[0];
      }
      
      return `${personnelId}-${dateStr}-${startTime}`;
    };

    const getLocalDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

const updateLocalRotaEntry = (personnelId: string, date: Date, status: string, notes?: string) => {
  const timeSlot = getTimeSlotForStatus(status);
  const dateStr = getLocalDateString(date);
  
  const newEntry: RotaEntry = {
    personnelId,
    date: dateStr,
    startTime: timeSlot.startTime,
    endTime: timeSlot.endTime,
    status,
    notes: notes || '',
    isLocal: true,
    isNew: true,
    isModified: true
  };

  setRotaEntries(prev => {
  const filtered = prev.filter(e =>
    !(
      (typeof e.personnelId === 'string' ? e.personnelId : e.personnelId._id) === personnelId &&
      e.date === dateStr &&
      e.startTime === timeSlot.startTime &&
      e.endTime === timeSlot.endTime
    )
  );
  return [...filtered, newEntry];
});
  setHasUnsavedChanges(true);
};

const getRotaEntry = (
  personnelId: string, 
  date: Date, 
  startTime: string, 
  endTime?: string
): RotaEntry | null => {
  const dateStr = getLocalDateString(date);
  return rotaEntries.find(entry => 
    entry.personnelId === personnelId && 
    entry.date === dateStr && 
    entry.startTime === startTime &&
    (!endTime || entry.endTime === endTime)
  ) || null;
};

    const deleteLocalRotaEntry = (personnelId: string, date: Date, startTime: string, endTime?: string) => {
  const dateStr = getLocalDateString(date);
  
  setRotaEntries(prev => 
    prev.filter(entry => 
      !(entry.personnelId === personnelId && 
        entry.date === dateStr && 
        entry.startTime === startTime)
    )
  );
  
  setHasUnsavedChanges(true);
};

    // Database deletion function
   const deleteFromDatabase = async (personnelId: string, date: Date, startTime: string) => {
  try {
    setDeleting(true);
    const dateStr = getLocalDateString(date);
    
    await axiosInstance.delete(`/api/rota/entry/${personnelId}/${dateStr}/${startTime}`);
    
    setRotaEntries(prev => 
      prev.filter(entry => 
        !(entry.personnelId === personnelId && 
          entry.date === dateStr && 
          entry.startTime === startTime)
      )
    );
    
  } catch (error: any) {
    setError(error.response?.data?.message || 'Failed to delete entry from database');
    throw error;
  } finally {
    setDeleting(false);
  }
};

    // Smart delete that handles both local and database entries
    const handleDeleteEntry = async (event: CalendarEvent) => {
      // Don't delete holiday events
      if (event.extendedProps.isHoliday) return;
      // All-hands: only gm/superadmin can delete
      if (event.extendedProps.isAllHands) {
        if (user?.role === 'superadmin' || user?.role === 'gm') {
          if (window.confirm(`Delete staff meeting "${event.extendedProps.allHandsTitle}"?`)) {
            await handleDeleteAllHands(event.extendedProps.entryId!);
          }
        }
        return;
      }
      if (event.extendedProps.isPending) {
  setError('Pending leave requests cannot be deleted from the rota. Please cancel the request from the Leave Requests page.');
  return;
}
      
      const { personnelId, startTime, isLocal } = event.extendedProps;
      const date = new Date(event.start);

      if (isLocal) {
        deleteLocalRotaEntry(personnelId, date, startTime);
      } else {
        setEntryToDelete(event);
        setDeleteDialogOpen(true);
      }
    };

    // Confirm database deletion
    const confirmDatabaseDeletion = async () => {
      if (!entryToDelete) return;
      
      try {
        const { personnelId, startTime } = entryToDelete.extendedProps;
        const date = new Date(entryToDelete.start);
        
        await deleteFromDatabase(personnelId, date, startTime);
        
        setDeleteDialogOpen(false);
        setEntryToDelete(null);
        
      } catch (error) {
        // Error already handled in deleteFromDatabase
      }
    };

    // Handle Time In Lieu confirmation
const handleTilConfirm = () => {
  if (!tilDate) return;

  if (tilStartTime >= tilEndTime) {
    setError('End time must be after start time');
    return;
  }

  // ✅ Create one local custom slot (Cooler/Event etc.)
  createCustomTimeEntry(
    selectedPersonnel,
    tilDate,
    tilStartTime,
    tilEndTime,
    customStatusId!,
    customStatusId === 'meeting' ? meetingNotes : undefined
  );

  // ✅ Ensure the new entry is marked properly for save
  setRotaEntries(prev =>
    prev.map(e =>
      e.isLocal && !e._id
        ? { ...e, isNew: true, isModified: true }
        : e
    )
  );

  // ✅ Close and reset
  setCustomTimeDialogOpen(false);
  setCustomStatusId(null);
  setTilDate(null);
  setTilStartTime('10:00');
  setTilEndTime('13:00');
  setMeetingNotes('');
};

    // All-hands meeting submit
    const handleAllHandsConfirm = async () => {
      if (!allHandsDate || !allHandsTitle.trim()) return;
      if (allHandsStartTime >= allHandsEndTime) {
        setError('End time must be after start time');
        return;
      }
      try {
        setSavingAllHands(true);
        const dateStr = getLocalDateString(allHandsDate);
        const response = await axiosInstance.post('/api/rota/all-hands', {
          date: dateStr,
          title: allHandsTitle.trim(),
          notes: allHandsNotes,
          startTime: allHandsStartTime,
          endTime: allHandsEndTime
        });
        const newEntry: AllHandsEntry = {
          _id: response.data._id,
          date: dateStr,
          title: allHandsTitle.trim(),
          notes: allHandsNotes,
          startTime: allHandsStartTime,
          endTime: allHandsEndTime,
          isAllHands: true
        };
        setAllHandsEntries(prev => [...prev, newEntry]);
        setCalendarKey(k => k + 1);
        setAllHandsDialogOpen(false);
        setAllHandsTitle('');
        setAllHandsNotes('');
        setAllHandsStartTime('10:00');
        setAllHandsEndTime('11:00');
        setAllHandsDate(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to create all-hands meeting');
      } finally {
        setSavingAllHands(false);
      }
    };

    const handleDeleteAllHands = async (entryId: string) => {
      try {
        await axiosInstance.delete(`/api/rota/all-hands/${entryId}`);
        setAllHandsEntries(prev => prev.filter(e => e._id !== entryId));
        setCalendarKey(k => k + 1);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete meeting');
      }
    };

    // Pattern confirmation
    const handlePatternConfirm = (applyToAll: boolean, skipExisting: boolean = true) => {
      if (!patternDay || !patternOption) return;

      if (applyToAll) {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const targetDayOfWeek = patternDay.date.getDay();
        
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month, day);
          if (date.getDay() === targetDayOfWeek) {
            const patternTimeSlot = getTimeSlotForStatus(patternOption);
            const existingEntry = getRotaEntry(selectedPersonnel, date, patternTimeSlot.startTime);
            
            if (skipExisting && existingEntry) {
              continue;
            }
            
            updateLocalRotaEntry(selectedPersonnel, date, patternOption);
          }
        }
      } else {
        updateLocalRotaEntry(selectedPersonnel, patternDay.date, patternOption);
      }

      setShowPatternDialog(false);
      setPatternOption(null);
      setPatternDay(null);
    };

    // Save to database (includes custom time slots)
const handleSave = async () => {
  try {
    setSaving(true);
    setError(null);

    // ✅ Include local entries so Cooler/Event slots are saved
    const changedEntries = rotaEntries.filter(
      e => e.isNew || e.isModified || e.isLocal
    );

    if (changedEntries.length === 0) {
      setHasUnsavedChanges(false);
      setSaving(false);
      return;
    }

    const customEntries: RotaEntry[] = [];
    const standardEntries: RotaEntry[] = [];

    changedEntries.forEach(entry => {
      const standardTimeSlot = getTimeSlotForStatus(entry.status);
      if (
        entry.startTime === standardTimeSlot.startTime &&
        entry.endTime === standardTimeSlot.endTime
      ) {
        standardEntries.push(entry);
      } else {
        customEntries.push(entry);
      }
    });

    if (standardEntries.length > 0) {
      const entriesToSave = standardEntries.map(entry => ({
        personnelId:
          typeof entry.personnelId === 'string'
            ? entry.personnelId
            : entry.personnelId._id,
        date: entry.date,
        status: entry.status,
        notes: entry.notes || ''
      }));

      await axiosInstance.post('/api/rota/bulk', { entries: entriesToSave });
    }

    if (customEntries.length > 0) {
      // ✅ Save each custom entry (Cooler/Event etc.)
      for (const entry of customEntries) {
        await axiosInstance.post('/api/rota/custom-slot', {
          personnelId:
            typeof entry.personnelId === 'string'
              ? entry.personnelId
              : entry.personnelId._id,
          date: entry.date,
          status: entry.status,
          startTime: entry.startTime,
          endTime: entry.endTime,
          notes: entry.notes || ''
        });
      }
    }

    // ✅ Reload data after saving
    await Promise.all([loadRotaData(), loadPendingRequests()]);
    setHasUnsavedChanges(false);
  } catch (err: any) {
    console.error('❌ Error saving rota:', err);
    setError(err.response?.data?.message || 'Failed to save roster');
  } finally {
    setSaving(false);
  }
};

    // ⭐ FIXED: Get holiday events for calendar display - prevents timezone issues
    const getHolidayEvents = (): CalendarEvent[] => {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const holidays = getHolidaysInRange(startDate, endDate);
      
      return holidays.map(holiday => {
        // ⭐ CRITICAL FIX: Format date in local timezone to prevent shifting
        const year = holiday.date.getFullYear();
        const month = String(holiday.date.getMonth() + 1).padStart(2, '0');
        const day = String(holiday.date.getDate()).padStart(2, '0');
        const localDateStr = `${year}-${month}-${day}`;
        
        console.log('🇮🇪 Holiday:', holiday.name, 'Date:', holiday.date.toDateString(), 'ISO:', localDateStr);
        
        return {
          id: `holiday-${holiday.id}`,
          title: `🇮🇪 ${holiday.name}`,
          start: localDateStr,  // ⭐ FIXED: Use local date string instead of ISO
          end: localDateStr,    // ⭐ FIXED: Use local date string instead of ISO
          allDay: true,
          backgroundColor: 'transparent',
          textColor: '#42A5F5',
          borderColor: '#42A5F5',
          extendedProps: {
            personnelId: '',
            status: '',
            startTime: '',
            endTime: '',
            isHoliday: true,
            holidayName: holiday.name,
            personnel: {} as Personnel
          }
        };
      });
    };

const getPendingLeaveEvents = (): CalendarEvent[] => {
  if (!Array.isArray(pendingRequests) || pendingRequests.length === 0) return [];

  const events: CalendarEvent[] = [];

  pendingRequests.forEach((request: any) => {
    const personId = request.personnelId?._id || request.personnelId;
    const person = personnel.find(p => p._id === personId);
    if (!person) return;

    // 🔒 Only render *real* pending requests
    if (request.status !== 'pending') return;

    const reqType = request.type || request.leaveType;
    if (!reqType) return;

    const dateRaw = request.dateStart || request.date || request.startDate;
    if (!dateRaw) return;

    // ⭐ FIXED: Prevent timezone shift — force to local calendar day
const d = new Date(dateRaw);
const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
const dateStr = local.toISOString().split('T')[0];

    // Calculate duration safely
    let startTime = '10:00';
    let endTime = '18:00';

    const hrs = Number(request.hoursRequested ?? request.hours ?? 0);
    if (reqType === 'time-lieu' && hrs > 0 && hrs <= 8) {
      const endMinutes = 600 + Math.round(hrs * 60);
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
    }

    // Guard against duplicates already present in rotaEntries
const key = `${personId}-${dateStr}-${startTime}`;
console.log('🔍 Checking for duplicates:', { personId, dateStr, key });
console.log('🔍 Existing rota keys:', Object.keys(rotaEntries).filter(k => k.includes(personId)));
const existingLeaveEntry = rotaEntries.find(entry => {
  const entryPersonId = typeof entry.personnelId === 'string' ? entry.personnelId : entry.personnelId._id;
  const entryDate = typeof entry.date === 'string' ? entry.date.split('T')[0] : new Date(entry.date).toISOString().split('T')[0];
  return (
    entryPersonId === personId &&
    entryDate === dateStr &&
    (entry.status === 'annual-leave' || entry.status === 'time-lieu')
  );
});

if (existingLeaveEntry) {
  console.log('❌ SKIPPING - leave entry already exists for this date');
  return;
}

    const title =
      viewMode === 'multi'
        ? `${person.name}: Pending ${reqType === 'annual-leave' ? 'AL' : 'TIL'}`
        : `⏳ Pending ${reqType === 'annual-leave' ? 'Annual Leave' : 'TIL'} (${startTime}-${endTime})`;

    events.push({
      id: `pending-${request._id}`,
      title,
      start: `${dateStr}T${startTime}:00`,
      end: `${dateStr}T${endTime}:00`,
      allDay: false,
      backgroundColor: '#FF9800',
      textColor: '#ffffff',
      borderColor: '#F57C00',
      extendedProps: {
        personnelId: personId,
        status: 'pending-leave',
        startTime,
        endTime,
        notes: `Pending approval - ${reqType}`,
        isLocal: false,
        personnel: person,
        isPending: true,
        leaveRequestId: request._id
      }
    });
  });
console.log('🎨 Converting pending requests to calendar events:', events);
  return events;
};

const PENDING_COLOR = '#FF9800';
    // Convert to calendar events (includes holidays)
const convertToCalendarEvents = (): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  // Add regular rota entries
  rotaEntries.forEach((entry) => {
    const entryPersonnelId = typeof entry.personnelId === 'string' 
      ? entry.personnelId 
      : entry.personnelId._id;
    const person = personnel.find(p => p._id === entryPersonnelId);
    
    if (!person) return;
    
    if (viewMode === 'single' && entryPersonnelId !== selectedPersonnel) {
      return;
    }
    
    const option = getRotaOptionById(entry.status);
    if (!option) return;

    let dateStr = entry.date;
    if (typeof dateStr !== 'string') {
      dateStr = new Date(entry.date).toISOString().split('T')[0];
    } else if (dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }
    
    const startDateTime = `${dateStr}T${entry.startTime}:00`;
    const endDateTime = `${dateStr}T${entry.endTime}:00`;

  const title = viewMode === 'multi' 
  ? `${option.shortLabel}: ${person.name.split(' ')[0]} ${person.name.split(' ')[1]?.[0]} (${entry.startTime}-${entry.endTime})`
  : `${option.shortLabel} (${entry.startTime}-${entry.endTime})`;

    const calendarEvent: CalendarEvent = {
      id: `${entryPersonnelId}-${dateStr}-${entry.startTime}-${entry.status}`,
      title,
      start: startDateTime,  
      end: endDateTime,      
      allDay: false,         
      backgroundColor: getMuiColorForOption(entry.status),
      textColor: '#ffffff',
      borderColor: entry.isLocal ? '#FFC107' : getMuiColorForOption(entry.status),
      extendedProps: {
        personnelId: entryPersonnelId,
        status: entry.status,
        startTime: entry.startTime,
        endTime: entry.endTime,
        notes: entry.notes,
        isLocal: entry.isLocal,
        personnel: person,
        entryId: entry._id
      }
    };

    events.push(calendarEvent);
  });

  // Add all-hands events — show on every calendar view
  allHandsEntries.forEach(entry => {
    let dateStr = entry.date;
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    events.push({
      id: `allhands-${entry._id}`,
      title: `📅 ${entry.title} (${entry.startTime}-${entry.endTime})`,
      start: `${dateStr}T${entry.startTime}:00`,
      end: `${dateStr}T${entry.endTime}:00`,
      allDay: false,
      backgroundColor: '#6d28d9',
      textColor: '#ffffff',
      borderColor: '#5b21b6',
      extendedProps: {
        personnelId: '',
        status: 'staff-meeting',
        startTime: entry.startTime,
        endTime: entry.endTime,
        notes: entry.notes,
        isLocal: false,
        personnel: {} as Personnel,
        entryId: entry._id,
        isAllHands: true,
        allHandsTitle: entry.title
      }
    });
  });

  // Add holiday events
  const holidayEvents = getHolidayEvents();
  events.push(...holidayEvents);
  const pendingEvents = getPendingLeaveEvents();
  events.push(...pendingEvents);

  return events;
};

    // Event handlers
    const handleDateSelect = (selectInfo: DateSelectArg) => {
      const selectedDate = new Date(selectInfo.start);
      setSelectedDay(selectedDate);
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
      // Don't allow clicking on holiday events
      if (clickInfo.event.extendedProps.isHoliday) return;
      
      const event = clickInfo.event;
      const personnelId = event.extendedProps.personnelId;
      const date = new Date(event.start!);
      
      setSelectedPersonnel(personnelId);
      setSelectedDay(date);
    };

    // Option handling with Time In Lieu dialog
  const handleOptionClick = (optionId: string, date: Date) => {
    if (!date) return;
    
  if (optionId === 'delete') {
  // Delete ALL entries for this person on this date
  const dateStr = getLocalDateString(date);
  setRotaEntries(prev => 
    prev.filter(entry => 
      !(entry.personnelId === selectedPersonnel && entry.date === dateStr)
    )
  );
  setHasUnsavedChanges(true);
  return;
}

    // ⭐ NEW: Intercept annual-leave selection to show leave dialog
  if (['annual-leave', 'time-lieu'].includes(optionId)) {
    setLeaveDialogDate(date);
    setLeaveDialogOpen(true);
    return;
  }

    // Special handling for Time In Lieu and other custom time entries
    if ([ 'traveling', 'working-unavail', 'meeting'].includes(optionId)) {
      setTilDate(date);
      setCustomStatusId(optionId);
      setCustomTimeDialogOpen(true);
      return;
    }

    if (optionId === 'event') {
    setTilDate(date);  // reuse existing state
    setCustomStatusId('event');
    setCustomTimeDialogOpen(true);  // open the same custom dialog
    return;
  }
  if (optionId === 'cooler') {
  setTilDate(date);
  setCustomStatusId('cooler');
  setTilStartTime('15:00');
  setTilEndTime('23:00');
  setCustomTimeDialogOpen(true);
  return;
} 

    if (shouldShowPatternDialog(optionId, date)) {
      const dayOfWeek = date.getDay();
      const dayName = getDayName(dayOfWeek);
      
      setPatternOption(optionId);
      setPatternDay({ date, dayOfWeek, dayName });
      setShowPatternDialog(true);
    } else {
      updateLocalRotaEntry(selectedPersonnel, date, optionId);
    }
  };

  // 4. Add new handler for leave dialog confirmation
const handleLeaveConfirm = async (
  _leaveType: 'annual-leave' | 'time-lieu',
  _hours?: number,
  _timeSlot?: { startTime: string; endTime: string }
) => {
  try {
    // The dialog already created the LeaveRequest in the DB.
    // Here we just close and refresh pending overlays.
    setLeaveDialogOpen(false);
    setLeaveDialogDate(null);

    // Refresh the "⏳ PENDING" events from the server
    await loadPendingRequests();
  } catch (err: any) {
    setError(err.response?.data?.message || 'Failed to refresh pending requests');
  }
};

    // Bulk operations
  const fillMonthWithWorking = () => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    if (isWeekday(date)) {
      // Check if this date is an Irish bank holiday
      if (isIrishHoliday(date)) {
        updateLocalRotaEntry(selectedPersonnel, date, 'bank-holiday');
      } else {
        updateLocalRotaEntry(selectedPersonnel, date, 'working');
      }
    }
  }
};

  const clearMonth = () => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateStr = getLocalDateString(date);
    
    // Remove all entries for this person on this date
    setRotaEntries(prev => 
      prev.filter(entry => 
        !(entry.personnelId === selectedPersonnel && entry.date === dateStr)
      )
    );
  }
  
  setHasUnsavedChanges(true);
};

    const handleRefresh = async () => {
      if (hasUnsavedChanges) {
        const confirm = window.confirm('You have unsaved changes. Refreshing will discard them. Continue?');
        if (!confirm) return;
      }
      await Promise.all([loadRotaData(), loadPendingRequests()]);
    };

    const navigateMonth = (direction: number) => {
      if (hasUnsavedChanges) {
        const confirm = window.confirm('You have unsaved changes. Changing months will discard them. Continue?');
        if (!confirm) return;
      }

      const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1);
      console.log('🗓️ Navigating from', currentMonth.toDateString(), 'to', newDate.toDateString());
      
      setCurrentMonth(newDate);
      setCalendarKey(prev => prev + 1);
    };

    // Pattern dialog helpers
    const getExistingDatesForPattern = (dayOfWeek: number): string[] => {
      if (!patternDay || !patternOption) return [];
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const timeSlot = getTimeSlotForStatus(patternOption);
      const existingDates: string[] = [];
      
      const targetDayOfWeek = patternDay.date.getDay();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        if (date.getDay() === targetDayOfWeek && getRotaEntry(selectedPersonnel, date, timeSlot.startTime)) {
          existingDates.push(date.toISOString().split('T')[0]);
        }
      }
      
      return existingDates;
    };

    const getAffectedDatesForPattern = (dayOfWeek: number): string[] => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const targetDayOfWeek = patternDay ? patternDay.date.getDay() : dayOfWeek;
      
      const affectedDates: string[] = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        if (date.getDay() === targetDayOfWeek) {
          affectedDates.push(date.toISOString().split('T')[0]);
        }
      }
      
      return affectedDates;
    };

  useEffect(() => {
    if (shouldScrollToCalendar) {
      const interval = setInterval(() => {
        const calendarNode = calendarRef.current?.querySelector('.fc-view-harness');
        if (calendarNode) {
          calendarNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
          clearInterval(interval);
          setShouldScrollToCalendar(false);
        }
      }, 50); // Poll every 50ms until calendar is ready

      return () => clearInterval(interval); // cleanup
    }
  }, [calendarKey, shouldScrollToCalendar]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scroll') === 'calendar') {
      setShouldScrollToCalendar(true);
    }
  }, [location.search]);

    const monthName = format(currentMonth, 'MMMM yyyy');
    const selectedPersonnelObj = personnel.find(p => p._id === selectedPersonnel);
    const calendarEvents = convertToCalendarEvents();

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading Rota V2...</Typography>
        </Box>
      );
    }

    // --- Summary metrics (place above return) ---
const toDay = (d: string | Date) =>
  typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];

const hoursBetween = (start: string, end: string) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm)) / 60;
};

const workingStatuses = ['working','programming','am','pm','event','cooler','working-unavail'];

const personEntries = rotaEntries.filter(e =>
  (typeof e.personnelId === 'string' ? e.personnelId : e.personnelId._id) === selectedPersonnel
);

// unique days helper
const uniqueDayCount = (entries: typeof personEntries) =>
  new Set(entries.map(e => toDay(e.date))).size;

// counts
const workingDays = uniqueDayCount(personEntries.filter(e => workingStatuses.includes(e.status)));
const annualLeaveDays = uniqueDayCount(personEntries.filter(e => e.status === 'annual-leave'));
const sickLeaveDays = uniqueDayCount(personEntries.filter(e => e.status === 'sick-leave'));
const bankHolidayDays = uniqueDayCount(personEntries.filter(e => e.status === 'bank-holiday'));
const amShifts = personEntries.filter(e => e.status === 'am').length;
const pmShifts = personEntries.filter(e => e.status === 'pm').length;

// hours
const totalHours = personEntries.reduce((sum, e) => sum + hoursBetween(e.startTime, e.endTime), 0);
const tilHours = personEntries
  .filter(e => e.status === 'time-lieu')
  .reduce((sum, e) => sum + hoursBetween(e.startTime, e.endTime), 0);

    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <WhosWorkingToday variant="timeline" />
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>You have unsaved changes.</strong> Don't forget to click "Save Roster".
            </Typography>
          </Alert>
        )}

        {/* Controls */}
        <Paper sx={{ mb: 3, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Users color="#3A9188" />
                <FormControl fullWidth>
                  <InputLabel>View Mode</InputLabel>
                  <Select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value as 'single' | 'multi')}
                    label="View Mode"
                  >
                    <MenuItem value="single">Single Staff</MenuItem>
                    <MenuItem value="multi">All Staff</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {viewMode === 'single' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Staff Member</InputLabel>
                  <Select
                    value={selectedPersonnel}
                    onChange={(e) => setSelectedPersonnel(e.target.value)}
                    label="Staff Member"
                  >
                    {personnel.map(person => (
                      <MenuItem key={person._id} value={person._id}>
                        {person.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}


            <Grid item xs={12} md={6}>
  <Stack direction="row" spacing={2} justifyContent="flex-end">
    <Button
      onClick={fillMonthWithWorking}
      variant="contained"
      size="small"
      disabled={!selectedPersonnel || viewMode === 'multi'}
      sx={{
        px: 2,
        py: 0.5,
        bgcolor: '#3A9188',
        '&:hover': { bgcolor: '#2D7A72' }
      }}
    >
      Fill Month
    </Button>
    <Button
      onClick={clearMonth}
      variant="outlined"
      size="small"
      disabled={!selectedPersonnel || viewMode === 'multi'}
    >
      Clear
    </Button>
    <Button
      onClick={handleRefresh}
      variant="outlined"
      size="small"
      startIcon={<RefreshCw size={16} />}
    >
      Refresh
    </Button>
  </Stack>
              </Grid>
          </Grid>
        </Paper>

{/* Roster Options */}
<Paper sx={{ mb: 3, p: 3 }}>
  <Typography variant="h6" gutterBottom>
    Rota Options - {selectedDay ? `Selected: ${format(selectedDay, 'EEEE, MMMM d')}` : 'Click a date first'}
    {selectedPersonnelObj && ` (${selectedPersonnelObj.name})`}
    {selectedDay && isIrishHoliday(selectedDay) && (
      <Chip 
        label={`🇮🇪 ${getHolidayInfo(selectedDay)?.name}`}
        size="small" 
        color="info" 
        sx={{ ml: 1 }}
      />
    )}
  </Typography>

  <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }}>
    {ROTA_GROUPS.map((group, groupIndex) => (
      <Box key={group.name}>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            mb: 1, 
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontSize: '0.7rem'
          }}
        >
          {group.name}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {group.options.map(optionId => {
            const option = getRotaOptionById(optionId);
            if (!option) return null;
            
            return (
              <Button
                key={option.id}
                onClick={() => selectedDay && handleOptionClick(option.id, selectedDay)}
                disabled={!selectedDay || !selectedPersonnel}
                size="small"
                title={option.description}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 'medium',
                  bgcolor: selectedDay ? getMuiColorForOption(option.id) : 'grey.300',
                  color: 'white',
                  '&:hover': {
                    bgcolor: selectedDay ? getMuiColorForOption(option.id) : 'grey.300',
                    opacity: 0.9
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.600'
                  }
                }}
              >
                {option.shortLabel}
              </Button>
            );
          })}
        </Stack>
      </Box>
    ))}
  </Stack>

  {selectedDay && isIrishHoliday(selectedDay) && (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      💡 Working on bank holidays? Time in lieu applies as per contract.
    </Typography>
  )}

  {(user?.role === 'superadmin' || user?.role === 'gm') && (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.7rem' }}>
        All-Hands (GM / SuperAdmin)
      </Typography>
      <Button
        onClick={() => {
          if (!selectedDay) return;
          setAllHandsDate(selectedDay);
          setAllHandsDialogOpen(true);
        }}
        disabled={!selectedDay}
        size="small"
        variant="contained"
        sx={{ bgcolor: '#6d28d9', '&:hover': { bgcolor: '#5b21b6' }, fontSize: '0.75rem' }}
      >
        📅 Create Meeting
      </Button>
    </Box>
  )}
</Paper>
<Grid item xs={12} md={6}>
  <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
    <Select
      value={currentMonth.getFullYear()}
      onChange={(e) => {
        const newDate = new Date(Number(e.target.value), currentMonth.getMonth(), 1);
        setCurrentMonth(newDate);
        setCalendarKey(prev => prev + 1);
        setShouldScrollToCalendar(true);
      }}
      size="small"
      sx={{
        minWidth: 100,
        color: 'white',
        '.MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
        '.MuiSvgIcon-root': { color: 'white' }
      }}
    >
      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
        <MenuItem key={year} value={year}>{year}</MenuItem>
      ))}
    </Select>
    
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
      {MONTHS.map((label, index) => (
        <Button
          key={label}
          size="small"
          variant={currentMonth.getMonth() === index ? 'contained' : 'outlined'}
          onClick={() => {
            const newDate = new Date(currentMonth);
            newDate.setMonth(index);

            const sameMonth = currentMonth.getMonth() === index;

            setCurrentMonth(newDate);

            if (!sameMonth) {
              setCalendarKey(prev => prev + 1);
              setShouldScrollToCalendar(true);
            }
          }}
          sx={{
            minWidth: 40,
            px: 1,
            py: 0.5,
            fontSize: '1.1rem',
            color: currentMonth.getMonth() === index ? 'white' : '#D1D5DB',
            bgcolor: currentMonth.getMonth() === index ? '#3A9188' : 'transparent',
            borderColor: '#374151',
            '&:hover': {
              bgcolor: currentMonth.getMonth() === index ? '#2D7A72' : 'rgba(255,255,255,0.1)',
            }
          }}
        >
          {label}
        </Button>
      ))}
    </Stack>
  </Stack>
</Grid>

  <div ref={calendarRef}>
  <Paper
    sx={{ overflow: 'hidden', mb: 3, scrollMarginTop: '100px' }} // Adjust margin to match header height
  >
              <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={currentMonth}
            timeZone="local"  // ⭐ CRITICAL: Force local timezone
            datesSet={(dateInfo) => {
              const mid = new Date((dateInfo.start.getTime() + dateInfo.end.getTime()) / 2);
              const newYear = mid.getFullYear();
              const newMonth = mid.getMonth();
              if (newYear !== currentMonth.getFullYear() || newMonth !== currentMonth.getMonth()) {
                setCurrentMonth(new Date(newYear, newMonth, 1));
              }
            }}
            headerToolbar={{
            left: 'prev,next today',
            center: 'title',         // ⭐ REMOVED: prev,next
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
            events={calendarEvents}
            selectable={true}
            selectMirror={true}
            select={handleDateSelect}
            eventClick={handleEventClick}
            height="600px"
            weekends={true}
            dayMaxEvents={false}
            eventDisplay="block"
            eventTextColor="#ffffff"
            slotMinTime="08:00:00"
            slotMaxTime="24:00:00"
            slotDuration="01:00:00"
            slotLabelInterval="02:00:00"
            allDaySlot={true}
            nowIndicator={true}
            scrollTime="09:00:00"
            forceEventDuration={true}
            // ⭐ NEW: Add holiday tooltip to day cells
            dayCellDidMount={(info) => {
              const date = info.date;
              if (isIrishHoliday(date)) {
                info.el.classList.add('fc-day-holiday');
                const holidayInfo = getHolidayInfo(date);
                if (holidayInfo) {
                  info.el.title = `Irish Bank Holiday: ${holidayInfo.name}`;
                  // Add subtle styling to holiday dates
                  info.el.style.backgroundColor = 'rgba(25, 118, 210, 0.10)';
                }
              }
            }}
            eventContent={(arg) => {
              // Don't show delete button for holiday events
              if (arg.event.extendedProps.isHoliday) {
                return (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    width: '100%',
                    minHeight: '20px',
                    padding: '1px 4px'
                  }}>
                    {arg.event.title}
                  </Box>
                );
              }
              if (arg.event.extendedProps.isPending) {
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 0.5,
      fontSize: '0.8rem',
      width: '100%',
      minHeight: '20px',
      padding: '1px 4px'
    }}>
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap' 
      }}>
        {arg.event.title}
        <Chip 
          label="⏳ PENDING" 
          size="small" 
          sx={{ 
            height: '16px', 
            fontSize: '0.6rem', 
            bgcolor: '#FFC107', 
            color: '#000',
            ml: 0.5,
            fontWeight: 'bold'
          }} 
        />
      </Box>
    </Box>
  );
}

              const isLocal = arg.event.extendedProps.isLocal;
              const DeleteIcon = isLocal ? X : Trash2;
              const deleteTitle = isLocal ? "Delete local entry" : "Delete database entry";
              
              return (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5, 
                    fontSize: '0.8rem',
                    width: '100%',
                    minHeight: '20px',
                    padding: '1px 4px'
                  }}
                >
                  <Box 
                    sx={{ 
                      flex: 1, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    {arg.event.title}
                    {/* ⭐ NEW: Show bank holiday badge on work events */}
                    {!arg.event.extendedProps.isHoliday && isIrishHoliday(new Date(arg.event.start!)) && (
                      <Chip 
                        label="BH" 
                        size="small" 
                        sx={{ 
                          height: '16px', 
                          fontSize: '0.6rem', 
                          bgcolor: '#1976d2', 
                          color: 'white',
                          ml: 0.5
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Box
                    title={deleteTitle}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      borderRadius: '3px',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '16px',
                      minHeight: '16px',
                      opacity: 0.7,
                      transition: 'opacity 0.2s ease',
                      '&:hover': { 
                        opacity: 1,
                        backgroundColor: 'rgba(255,255,255,0.5)' 
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteEntry(arg.event as any);
                    }}
                  >
                    <DeleteIcon size={10} />
                  </Box>
                </Box>
              );
            }}
            eventClassNames={(arg) => {
              const classes = ['custom-event'];
              if (arg.event.extendedProps.isLocal) {
                classes.push('local-event');
              }
              if (arg.event.extendedProps.isHoliday) {
                classes.push('holiday-event');
              }
              return classes;
            }}
            eventDidMount={(info) => {
              info.el.style.cursor = 'pointer';
              if (info.event.extendedProps.isHoliday) {
                // Holiday events styling
                info.el.style.borderWidth = '1px';
                info.el.style.borderStyle = 'solid';
                info.el.style.opacity = '0.8';
              } else {
                // Regular events styling
                info.el.style.borderWidth = info.event.extendedProps.isLocal ? '2px' : '1px';
                info.el.style.borderStyle = info.event.extendedProps.isLocal ? 'dashed' : 'solid';
              }
            }}
          />
        </Paper>
        </div>

        {/* Pattern Dialog */}
        <PatternDialog
          open={showPatternDialog}
          optionId={patternOption}
          dayName={patternDay?.dayName || ''}
          monthName={monthName}
          affectedDates={patternDay ? getAffectedDatesForPattern(patternDay.dayOfWeek) : []}
          existingDates={patternDay ? getExistingDatesForPattern(patternDay.dayOfWeek) : []}
          onConfirm={handlePatternConfirm}
          onCancel={() => {
            setShowPatternDialog(false);
            setPatternOption(null);
            setPatternDay(null);
          }}
        />

        {/* Time In Lieu Dialog */}
  <Dialog 
    open={customTimeDialogOpen} 
    onClose={() => {
      setCustomTimeDialogOpen(false);
      setCustomStatusId(null);
      setTilDate(null);
    }}
    maxWidth="sm"
    fullWidth
  >
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Clock color="#ca8a04" size={20} />
    {customStatusId === 'event'
      ? 'Custom Event Time'
      : getRotaOptionById(customStatusId || '')?.label || 'Custom Time Entry'}
  </DialogTitle>
    <DialogContent>
  <Typography variant="body2" sx={{ mb: 3 }}>
    {tilDate ? `Set custom hours for ${format(tilDate, 'EEEE, MMMM d, yyyy')}` : 'Select a date to continue'}
  </Typography>

  {customStatusId === 'event' && (
    <Alert severity="info" sx={{ mb: 2 }}>
      Is this a standard concert event (3 PM–11 PM)? If not, please specify the actual start and end time of the event.
    </Alert>
  )}
  {customStatusId === 'cooler' && (
    <Alert severity="info" sx={{ mb: 2 }}>
      Are you going to be in the Cooler for the day (10AM–6pm)? If not, please specify the actual start and end time you'll be there.
    </Alert>
  )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Start Time"
            type="time"
            value={tilStartTime}
            onChange={(e) => setTilStartTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="End Time"
            type="time"
            value={tilEndTime}
            onChange={(e) => setTilEndTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {customStatusId === 'meeting' && (
          <Grid item xs={12}>
            <TextField
              label="What is this meeting about?"
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Call with venue, artist check-in, budget review..."
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Please ensure the time range does not overlap with other entries.
      </Typography>
    </DialogContent>

    <DialogActions>
      <Button onClick={() => {
        setCustomTimeDialogOpen(false);
        setCustomStatusId(null);
        setTilDate(null);
      }}>
        Cancel
      </Button>
      <Button 
        onClick={handleTilConfirm} // Consider renaming to handleCustomTimeConfirm
        variant="contained"
        sx={{ bgcolor: '#ca8a04', '&:hover': { bgcolor: '#a16207' } }}
      >
        Apply {getRotaOptionById(customStatusId || '')?.shortLabel || 'Entry'}
      </Button>
    </DialogActions>
  </Dialog>

        {/* All-Hands Meeting Dialog */}
        <Dialog open={allHandsDialogOpen} onClose={() => setAllHandsDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calendar color="#6d28d9" size={20} />
            Create Staff Meeting — {allHandsDate ? format(allHandsDate, 'EEEE, MMMM d, yyyy') : ''}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This meeting will appear on everyone's rota and in the Slack daily summary.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Meeting Title"
                value={allHandsTitle}
                onChange={(e) => setAllHandsTitle(e.target.value)}
                fullWidth
                required
                placeholder="e.g. Weekly Staff Meeting, All-Hands Q2 Review..."
                inputProps={{ maxLength: 200 }}
                autoFocus
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={allHandsStartTime}
                    onChange={(e) => setAllHandsStartTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={allHandsEndTime}
                    onChange={(e) => setAllHandsEndTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Additional notes (optional)"
                value={allHandsNotes}
                onChange={(e) => setAllHandsNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Location, agenda, link to call..."
                inputProps={{ maxLength: 300 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAllHandsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAllHandsConfirm}
              variant="contained"
              disabled={!allHandsTitle.trim() || savingAllHands}
              sx={{ bgcolor: '#6d28d9', '&:hover': { bgcolor: '#5b21b6' } }}
            >
              {savingAllHands ? 'Saving...' : 'Create Meeting'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Trash2 color="#f44336" size={20} />
            Confirm Delete Entry
          </DialogTitle>
          <DialogContent>
            {entryToDelete && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete this rota entry?
                </Typography>
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper', 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 1,
                  mt: 2
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Entry Details:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Person:</strong> {entryToDelete.extendedProps.personnel.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong> {format(new Date(entryToDelete.start), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Time:</strong> {entryToDelete.extendedProps.startTime} - {entryToDelete.extendedProps.endTime}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {getRotaOptionById(entryToDelete.extendedProps.status)?.label}
                  </Typography>
                </Box>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    This will permanently remove the entry from the database.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDatabaseDeletion}
              variant="contained" 
              color="error"
              startIcon={deleting ? <CircularProgress size={16} /> : <Trash2 size={16} />}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Entry'}
            </Button>
          </DialogActions>
        </Dialog>

{/* Summary */}
{selectedPersonnelObj && viewMode === 'single' && (
  <Paper sx={{ mb: 3, p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Month Summary for {selectedPersonnelObj.name}
      {hasUnsavedChanges && (
        <Chip label="Local Preview" color="warning" size="small" sx={{ ml: 1 }} />
      )}
    </Typography>

    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      flexWrap="wrap"
      sx={{ mt: 1 }}
    >
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Working Days</Typography>
        <Typography variant="h6" color="#3A9188">{workingDays}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Annual Leave</Typography>
        <Typography variant="h6" color="#3A9188">{annualLeaveDays}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Time in Lieu</Typography>
        <Typography variant="h6" color="#3A9188">{tilHours.toFixed(1)}h</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Sick Leave</Typography>
        <Typography variant="h6" color="#3A9188">{sickLeaveDays}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Bank Holidays</Typography>
        <Typography variant="h6" color="#3A9188">{bankHolidayDays}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">AM Shifts</Typography>
        <Typography variant="h6" color="#3A9188">{amShifts}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">PM Shifts</Typography>
        <Typography variant="h6" color="#3A9188">{pmShifts}</Typography>
      </Box>

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 160 }}>
        <Typography variant="body2" color="text.secondary">Total Hours</Typography>
        <Typography variant="h6" color="#3A9188">{totalHours.toFixed(1)}h</Typography>
      </Box>
    </Stack>
  </Paper>
)}

        {/* Save Button */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Save size={20} />}
            sx={{ 
              bgcolor: hasUnsavedChanges ? '#FFC107' : '#3A9188',
              color: hasUnsavedChanges ? '#000' : '#fff',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 'bold',
              '&:hover': { 
                bgcolor: hasUnsavedChanges ? '#FFB300' : '#2D7A72' 
              },
              '&:disabled': {
                bgcolor: '#A4B0BE',
                color: '#fff'
              }
            }}
          >
            {saving ? 'Saving...' : hasUnsavedChanges ? 'Save Roster (Unsaved Changes)' : 'All Changes Saved'}
          </Button>
          
          {hasUnsavedChanges && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Click "Save Roster" to commit all your changes to the database
            </Typography>
          )}
        </Box>
        {/* Leave Selection Dialog */}
  <LeaveSelectionDialog
    open={leaveDialogOpen}
    personnelId={selectedPersonnel}
    selectedDate={leaveDialogDate}
    isSuperAdmin={user?.role === 'superadmin'}
    onConfirm={handleLeaveConfirm}
    onCancel={() => {
      setLeaveDialogOpen(false);
      setLeaveDialogDate(null);
    }}
  />
      </Container>
    );
  };

  export default RotaBuilder;